/**
 * Configures the cursor selection functionality for a canvas element.
 *
 * @param {HTMLCanvasElement} canvas - The canvas element to configure.
 */
function configureCursorSelection(canvas, onSelectionComplete) {
  let isDragging = false;
  let startX, endX;
  const ctx = canvas.getContext("2d");
  const width = canvas.offsetWidth;
  const height = canvas.offsetHeight;

  canvas.addEventListener("mousedown", function (event) {
    isDragging = true;
    startX = event.offsetX;
    endX = startX;

    ctx.clearRect(0, 0, width, height);
    drawVerticalLine(startX, 1);
  });

  canvas.addEventListener("mousemove", function (event) {
    if (isDragging) {
      endX = event.offsetX;

      ctx.clearRect(0, 0, width, height);
      drawVerticalLine(startX, 1);
      drawVerticalLine(endX, 0.3);
      drawTriangle(startX, endX);
    }
  });

  canvas.addEventListener("mouseup", function (event) {
    if (isDragging) {
      isDragging = false;
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = "rgba(255, 255, 255, 0.6)";
      ctx.fillRect(startX, 0, endX - startX, height);

      // Call the onSelectionComplete callback with the selected range
      onSelectionComplete(Math.min(startX, endX), Math.max(startX, endX));
    }
  });

  canvas.addEventListener("mouseleave", function (event) {
    if (isDragging) {
      isDragging = false;
    }
  });

  function drawVerticalLine(x, alpha) {
    ctx.strokeStyle = "rgba(255, 255, 255, " + alpha + ")";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  function drawTriangle(startX, endX) {
    const triangleWidth = 20;
    const triangleHeight = 30;
    const midY = height / 2;

    const direction = Math.sign(endX - startX);
    const tipX = endX;
    const baseX1 = endX - direction * triangleWidth;

    ctx.fillStyle = "white";
    ctx.beginPath();
    ctx.moveTo(tipX, midY);
    ctx.lineTo(baseX1, midY - triangleHeight / 2);
    ctx.lineTo(baseX1, midY + triangleHeight / 2);
    ctx.closePath();
    ctx.fill();
  }
}

/**
 * Calculates the time interval based on the input value.
 *
 * @param {number} input - The input value.
 * @returns {number} The calculated time interval.
 */
function calculateTimeInterval(input) {
  const normalizationFactor = 1250;
  const exponent = Math.log2(input / normalizationFactor);
  const result = Math.pow(2, exponent);
  return result;
}

/**
 * Returns the color object corresponding to the given value based on the provided spectrogram intensity threshold colors.
 *
 * @param {number} value - The value for which to determine the color.
 * @param {Array} spectrogramIntensityThresholdColors - An array of color objects representing the spectrogram intensity threshold colors.
 * @returns {Object} - The color object with properties r, g, b, and a representing the red, green, blue, and opacity values respectively.
 */
function getColorForValue(value, spectrogramIntensityThresholdColors) {
  // Check if the value is higher than the highest threshold
  if (value >= spectrogramIntensityThresholdColors[0].value) {
    const { red, green, blue, opacity } =
      spectrogramIntensityThresholdColors[0];
    return { r: red, g: green, b: blue, a: opacity };
  }

  // Iterate through the thresholds to find the correct range for the value
  for (let i = 1; i < spectrogramIntensityThresholdColors.length; i++) {
    if (value >= spectrogramIntensityThresholdColors[i].value) {
      const { red, green, blue, opacity } =
        spectrogramIntensityThresholdColors[i];
      return { r: red, g: green, b: blue, a: opacity };
    }
  }

  // If no threshold is met, return the lowest intensity color
  const { red, green, blue, opacity } =
    spectrogramIntensityThresholdColors[
      spectrogramIntensityThresholdColors.length - 1
    ];
  return { r: red, g: green, b: blue, a: opacity };
}

/**
 * Calculates the moving average of an array of data.
 *
 * @param {number[]} data - The input data array.
 * @param {number} windowSize - The size of the moving window.
 * @returns {number[]} - The array of smoothed values.
 */
function movingAverage(data, windowSize) {
  const smoothed = [];
  for (let i = 0; i < data.length; i++) {
    let sum = 0;
    let count = 0;
    for (
      let j = -Math.floor(windowSize / 2);
      j <= Math.floor(windowSize / 2);
      j++
    ) {
      if (i + j >= 0 && i + j < data.length) {
        sum += data[i + j];
        count++;
      }
    }
    smoothed.push(sum / count);
  }
  return smoothed;
}

/**
 * Applies the Blackman window to the given data array.
 *
 * @param {Float32Array} data - The input data array.
 * @returns {Float32Array} - The data array with the Blackman window applied.
 */
function applyBlackmanWindow(data) {
  const n = data.length;
  const window = new Float32Array(n);
  const a0 = 0.42;
  const a1 = 0.5;
  const a2 = 0.08;

  for (let i = 0; i < n; i++) {
    window[i] =
      a0 -
      a1 * Math.cos((2 * Math.PI * i) / (n - 1)) +
      a2 * Math.cos((4 * Math.PI * i) / (n - 1));
    data[i] *= window[i];

    if (isNaN(data[i])) {
      console.error(
        `NaN detected at index ${i} during Blackman window application`
      );
    }
  }

  //console.log("Blackman Window Applied Data:", data.slice(0, 10)); // Debug: Log windowed data

  return data;
}

/**
 * Pre-processes the given size to generate a table, width, bitrev, and csize.
 *
 * @param {number} size - The size to be pre-processed.
 * @returns {object} - An object containing the pre-processed values: table, width, bitrev, and csize.
 */
function preProcess(size) {
  size = size | 0;
  const csize = size << 1;
  const table = new Array(size * 2);
  for (let i = 0; i < table.length; i += 2) {
    const angle = (Math.PI * i) / size;
    table[i] = Math.cos(angle);
    table[i + 1] = -Math.sin(angle);
  }
  let power = 0;
  for (let t = 1; size > t; t <<= 1) power++;
  const width = power - 1;
  const bitrev = new Array(1 << width);
  for (let j = 0; j < bitrev.length; j++) {
    bitrev[j] = 0;
    for (let shift = 0; shift < width; shift += 2) {
      let revShift = width - shift - 2;
      bitrev[j] |= ((j >>> shift) & 3) << revShift;
    }
  }
  return {
    table,
    width,
    bitrev,
    csize,
  };
}

/**
 * Calculates the resonance solution for the given array of coefficients and order.
 *
 * @param {number[]} a - The array of coefficients.
 * @param {number} order - The order of the resonance solution.
 * @returns {number[]} - The calculated resonance solution.
 */
function resonanceSoln(a, order) {
  const n = a.length;
  const fm = order;
  const wk1 = new Array(n).fill(0);
  const wk2 = new Array(n).fill(0);
  const wkm = new Array(fm).fill(0);
  const d = new Array(fm).fill(0);
  const idata = new Array(n).fill(0);

  wk1[0] = a[0];
  wk2[n - 2] = a[n - 1];
  for (let j = 1; j < n - 1; j++) {
    wk1[j] = a[j];
    wk2[j - 1] = a[j];
  }

  for (let k = 0; k < fm; k++) {
    let num = 0;
    let dnom = 0;
    for (let j = 0; j <= n - k - 1; j++) {
      num += wk1[j] * wk2[j];
      dnom += wk1[j] * wk1[j] + wk2[j] * wk2[j];
    }
    d[k] = (2 * num) / dnom;

    for (let i = 0; i < k; i++) {
      d[i] = wkm[i] - d[k] * wkm[k - i - 1];
    }
    if (k === fm - 1) break;
    for (let i = 0; i <= k; i++) {
      wkm[i] = d[i];
    }
    for (let j = 0; j < n - k - 2; j++) {
      wk1[j] -= wkm[k] * wk2[j];
      wk2[j] = wk2[j + 1] - wkm[k] * wk1[j + 1];
    }
  }

  let nblowup = 0;
  for (let i = fm; i < n; i++) {
    let flaccum = 0;
    for (let j = 0; j < fm; j++) {
      flaccum += idata[i - j - 1] * d[j];
    }
    if (Math.abs(flaccum) > 2000000) {
      //console.log(flaccum);
      nblowup = 1;
      break;
    }
    idata[i] = i === fm ? 20000 : flaccum;
  }

  if (nblowup === 1) {
    //console.log('blowup');
    idata.fill(0);
  }

  return idata;
}

/**
 * Performs the Fast Fourier Transform (FFT) on the given data array.
 * 
 * @param {Float32Array} out - The output array to store the transformed values.
 * @param {Float32Array} data - The input data array.
 * @param {Float32Array} table - The lookup table used for the transformation.
 * @param {number} width - The width of the lookup table.
 * @param {number} csize - The size of the complex array.
 * @param {number[]} bitrev - The bit-reversed indices array.
 * @returns {Float32Array} - The transformed output array.
 */
function fft(out, data, table, width, csize, bitrev) {
  // Initial step (permute and transform)
  let inv = 0;
  let step = 1 << width;
  let len = (csize / step) << 1;

  let outOff;
  let t;
  for (outOff = 0, t = 0; outOff < csize; outOff += len, t++) {
    const off = bitrev[t];
    out = trf(outOff, off >>> 1, step >>> 1, out, data, inv);
  }

  // Loop through steps in decreasing order
  inv = 0 ? -1 : 1;
  for (step >>= 2; step >= 2; step >>= 2) {
    len = (csize / step) << 1;
    let halfLen = len >>> 1;
    let quarterLen = halfLen >>> 1;
    let hquarterLen = quarterLen >>> 1;
    // Loop through offsets in the data
    for (outOff = 0; outOff < csize; outOff += len) {
      for (let i = 0, k = 0; i <= hquarterLen; i += 2, k += step) {
        let A = outOff + i;
        let B = A + quarterLen;
        let C = B + quarterLen;
        let D = C + quarterLen;
        // Original values
        let Ar = out[A];
        let Ai = out[A + 1];
        let Br = out[B];
        let Bi = out[B + 1];
        let Cr = out[C];
        let Ci = out[C + 1];
        let Dr = out[D];
        let Di = out[D + 1];
        // Middle values
        let MAr = Ar;
        let MAi = Ai;
        let tableBr = table[k];
        let tableBi = inv * table[k + 1];
        let MBr = Br * tableBr - Bi * tableBi;
        let MBi = Br * tableBi + Bi * tableBr;
        let tableCr = table[2 * k];
        let tableCi = inv * table[2 * k + 1];
        let MCr = Cr * tableCr - Ci * tableCi;
        let MCi = Cr * tableCi + Ci * tableCr;
        let tableDr = table[3 * k];
        let tableDi = inv * table[3 * k + 1];
        let MDr = Dr * tableDr - Di * tableDi;
        let MDi = Dr * tableDi + Di * tableDr;
        // Pre-Final values
        let T0r = MAr + MCr;
        let T0i = MAi + MCi;
        let T1r = MAr - MCr;
        let T1i = MAi - MCi;
        let T2r = MBr + MDr;
        let T2i = MBi + MDi;
        let T3r = inv * (MBr - MDr);
        let T3i = inv * (MBi - MDi);
        // Final values
        let FAr = T0r + T2r;
        let FAi = T0i + T2i;
        let FBr = T1r + T3i;
        let FBi = T1i - T3r;
        out[A] = FAr;
        out[A + 1] = FAi;
        out[B] = FBr;
        out[B + 1] = FBi;
        // Output final middle point
        if (i === 0) {
          let FCr = T0r - T2r;
          let FCi = T0i - T2i;
          out[C] = FCr;
          out[C + 1] = FCi;
          continue;
        }
        // Do not overwrite ourselves
        if (i === hquarterLen) continue;
        let ST0r = T1r;
        let ST0i = -T1i;
        let ST1r = T0r;
        let ST1i = -T0i;
        let ST2r = -inv * T3i;
        let ST2i = -inv * T3r;
        let ST3r = -inv * T2i;
        let ST3i = -inv * T2r;
        let SFAr = ST0r + ST2r;
        let SFAi = ST0i + ST2i;
        let SFBr = ST1r + ST3i;
        let SFBi = ST1i - ST3r;
        let SA = outOff + quarterLen - i;
        let SB = outOff + halfLen - i;
        out[SA] = SFAr;
        out[SA + 1] = SFAi;
        out[SB] = SFBr;
        out[SB + 1] = SFBi;
      }
    }
  }
  return out;
}

/**
 * Transforms the input data array and stores the result in the output array.
 * @param {number} outOff - The starting index in the output array.
 * @param {number} off - The starting index in the input data array.
 * @param {number} step - The step size between elements in the input data array.
 * @param {number[]} out - The output array.
 * @param {number[]} data - The input data array.
 * @param {boolean} inv - Whether to apply inversion to the transformed values.
 * @returns {number[]} The transformed output array.
 */
function trf(outOff, off, step, out, data, inv) {
  const outt = out;
  const dataa = data;
  const invv = inv ? -1 : 1;
  const step2 = step * 2;
  const step3 = step * 3;

  // Original values
  const Ar = dataa[off];
  const Br = dataa[off + step];
  const Cr = dataa[off + step2];
  const Dr = dataa[off + step3];

  // Pre-Final values
  const T0r = Ar + Cr;
  const T1r = Ar - Cr;
  const T2r = Br + Dr;
  const T3r = invv * (Br - Dr);

  // Final values
  const FAr = T0r + T2r;

  const FBr = T1r;
  const FBi = -T3r;

  const FCr = T0r - T2r;

  const FDr = T1r;
  const FDi = T3r;

  outt[outOff] = FAr;
  outt[outOff + 1] = 0;
  outt[outOff + 2] = FBr;
  outt[outOff + 3] = FBi;
  outt[outOff + 4] = FCr;
  outt[outOff + 5] = 0;
  outt[outOff + 6] = FDr;
  outt[outOff + 7] = FDi;
  return outt;
}

/**
 * Performs Fast Fourier Transform (FFT) on the given data array and returns the magnitude spectrum in dB scale.
 *
 * @param {Float32Array} data - The input data array.
 * @returns {Float32Array} - The magnitude spectrum in dB scale.
 */
function performFFT(data) {
  const n = data.length;
  const out = new Array(n);

  // Preprocess the data
  const { table, width, bitrev, csize } = preProcess(n);

  // Perform FFT
  fft(out, data, table, width, csize, bitrev);

  // Convert the FFT output to magnitude spectrum
  const spectrum = new Float32Array(n / 2);
  for (let i = 0; i < n / 2; i++) {
    const real = out[2 * i];
    const imag = out[2 * i + 1];
    spectrum[i] = Math.sqrt(real * real + imag * imag);
  }

  // Convert to dB scale
  const dbSpectrum = spectrum.map((value) => 20 * Math.log10(value || 0.0001));

  return dbSpectrum;
}

/**
 * Calculates the resonance spectrum based on the given audio data, order, and FFT size.
 *
 * @param {Float32Array} tdAudioData - The audio data.
 * @param {number} order - The order of the resonance.
 * @param {number} fftSize - The size of the FFT.
 * @returns {Float32Array} The scaled resonance spectrum.
 */
function calculateResonanceSpectrum(tdAudioData, order, fftSize) {
  const windowedData = applyBlackmanWindow(tdAudioData);
  const impulseResponse = resonanceSoln(windowedData, order);

  const fftInput = new Float32Array(fftSize);
  fftInput.set(impulseResponse);

  const resonanceSpectrum = performFFT(fftInput);

  // Apply peak detection
  const peakThreshold = 0; // Adjust this value based on your desired peak prominence
  const peakProminence = 30; // Adjust this value based on your desired peak prominence
  const peaks = findPeaks(resonanceSpectrum, {
    threshold: peakThreshold,
    prominence: peakProminence,
  });

  // Create a new spectrum array with enhanced peaks
  const enhancedSpectrum = new Float32Array(resonanceSpectrum.length);
  enhancedSpectrum.set(resonanceSpectrum);

  // Enhance the peaks in the spectrum
  peaks.forEach((peak) => {
    const peakIndex = peak.index;
    const peakAmplitude = peak.amplitude;
    const enhancementFactor = 2; // Adjust this value to control the peak enhancement
    enhancedSpectrum[peakIndex] = peakAmplitude * enhancementFactor;
  });

  // Apply a scaling factor to further increase the amplitude
  const scalingFactor = 1.0; // Adjust this value as needed
  const scaledSpectrum = enhancedSpectrum.map(value => value * scalingFactor);

  return scaledSpectrum;
}

/**
 * Converts a frequency value to a musical note.
 *
 * @param {number} frequency - The frequency value to convert.
 * @returns {string} The musical note representation of the frequency.
 */
const frequencyToNote = (frequency) => {
	const pitchNames = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
	const baseFrequency = 16
	if(Number(frequency) >= baseFrequency) {
		const octave = Math.floor(Math.log2(Number(frequency)/baseFrequency));
		let pitchIndex = Math.round(12 * Math.log2(Number(frequency)/baseFrequency) % 12);
		// Handle case where pitch index equals 12
		if (pitchIndex === 12) pitchIndex = 0
		return pitchNames[pitchIndex] + octave
	}
};

const calculateSPLInstance = (rms, mindB, calibratedValue) => {
  let unCalibratedValue = null;
  let spl = 0;
  spl = 1 * (20 * Math.log10(rms));
  unCalibratedValue = spl;
  if (parseInt(calibratedValue) < 1)
    return {
      spl,
      unCalibratedValue,
    };
  spl = spl + (90 - parseInt(calibratedValue));
  if (spl < mindB) spl = 0;
  return {
    spl,
    unCalibratedValue,
  };
};

function calculateIntensity(timeDomainDataArray) {
  const sum = timeDomainDataArray.reduce((accumulator, element) => {
    const x = element / 1; // Adjust with volume slider if necessary
    return accumulator + x * x;
  }, 0);

  const rms = Math.sqrt(sum / timeDomainDataArray.length);
  const rms1 = 100 * rms;
  const minDbValue = 60;
  const splInstance = calculateSPLInstance(rms1, minDbValue);
  return splInstance?.spl * 1.135;
}

/**
 * Calculates the pitch using auto-correlation algorithm.
 *
 * @param {Float32Array} buf - The input buffer containing audio samples.
 * @param {number} sampleRate - The sample rate of the audio.
 * @returns {number} - The calculated pitch value.
 */

const autoCorrelate = (buf, sampleRate) => {
  // Auto-correlation algorithm to detect pitch
  const lastValidPitch = this.lastValidPitchRef.current;
  let SIZE = buf.length;
  let MAX_SAMPLES = Math.floor(SIZE / 2);
  let bestOffset = -1;
  let bestCorrelation = 0;
  let rms = 0;

  for (let i = 0; i < SIZE; i++) {
    let val = buf[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) return -1;

  let lastCorrelation = 1;
  for (let offset = 0; offset < MAX_SAMPLES; offset++) {
    let correlation = 0;

    for (let i = 0; i < MAX_SAMPLES; i++) {
      correlation += Math.abs((buf[i] - buf[i + offset]) * (buf[i] - buf[i + offset]));
    }
    correlation = 1 - correlation / MAX_SAMPLES;
    if (correlation > lastCorrelation && correlation > bestCorrelation) {
      bestCorrelation = correlation;
      bestOffset = offset;
    }
    lastCorrelation = correlation;
  }

  if (bestCorrelation > 0.01) {
    const currentPitch = sampleRate / bestOffset;
    
    // If we have a previous valid pitch to compare against
    if (lastValidPitch !== -1) {
      // Check if the new pitch differs by more than 100 from the last pitch
      if (Math.abs(currentPitch - lastValidPitch) > 5) {
        return lastValidPitch; // Return the previous pitch instead
      }
    }
    
    // Update the last valid pitch and return the current pitch
    lastValidPitch = currentPitch;

    console.log("Current Pitch:", currentPitch);

    return currentPitch;
  }

  return -1;
};

/**
 * Calculates the pixel value for a given frequency based on the canvas height.
 *
 * @param {number} frequency - The frequency value.
 * @param {number} canvasHeight - The height of the canvas.
 * @returns {number} The calculated pixel value.
 */
const getPixelValue = (ac, height) => {
  // Handle out of range frequencies
  if (ac < 55 || ac > 1760) {
    return height + 2; // Off-canvas
  }
  
  // Use the SAME positioning logic as axes.js for pitch charts
  // This matches the Y-axis marker positioning exactly
  const topPadding = 7;
  const availableHeight = height - topPadding;
  const markerValues = [1760, 880, 440, 220, 110, 55]; // Top to bottom (same as axes.js)
  const numSections = markerValues.length - 1; // 5 sections between 6 markers
  const sectionHeight = availableHeight / numSections;
  
  // Find which section this frequency falls into
  let targetY = height + 2; // Default to off-canvas
  
  for (let i = 0; i < markerValues.length - 1; i++) {
    const upperFreq = markerValues[i];     // Higher frequency (top)
    const lowerFreq = markerValues[i + 1]; // Lower frequency (bottom)
    
    if (ac <= upperFreq && ac >= lowerFreq) {
      // Linear interpolation within this section
      const sectionTop = topPadding + (i * sectionHeight);
      const sectionBottom = topPadding + ((i + 1) * sectionHeight);
      
      // Calculate position within section (linear interpolation)
      const ratio = (upperFreq - ac) / (upperFreq - lowerFreq);
      targetY = sectionTop + (ratio * sectionHeight);
      break;
    }
  }
  
  return Math.ceil(targetY);
}

const getNormalizedGain = (gain) => {
  const gainMapping = {
      10: 0.6,
      9: 0.7,
      8: 0.8,
      7: 0.9,
      6: 0.95,
      5: 1,
      4: 1.05,
      3: 1.1,
      2: 1.2,
      1: 1.3
  };
  return gainMapping[gain] || 1;
};

// Phones and tablets, for every gain decision in the app. Two things make this
// less obvious than a user-agent regex:
//
// 1. Since iPadOS 13, Safari on iPad reports itself as a desktop Mac
//    ("Macintosh; Intel Mac OS X") and carries NO iPad token in the user
//    agent, so a UA regex alone classifies every modern iPad as a desktop.
//    navigator.platform === 'MacIntel' plus a touch-capable maxTouchPoints is
//    the standard way to tell an iPad from a real Mac (Macs report 0).
// 2. The input gain and the playback gain used to detect this separately, and
//    only the playback one had the iPadOS probe. iPads therefore recorded at
//    the DESKTOP input gain (1.4 instead of 2.8) while their playback was
//    boosted normally, and retuning the mobile multiplier did nothing at all
//    on an iPad because the branch never ran. Fixed in 1.2.115 by routing both
//    through this single function; keep it that way.
const isMobileOrTablet = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    return true;
  }
  // iPad on iPadOS 13+, which presents a desktop-class user agent.
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
};

const DESKTOP_GAIN_BY_SLIDER = {
  1: 0.4,
  2: 0.6,
  3: 0.8,
  4: 1.0,
  5: 1.4,
  6: 1.6,
  7: 1.8,
  8: 2.0,
  9: 2.2,
  10: 2.4
};

// Slider position the app defaults to.
const DEFAULT_GAIN_SLIDER = 5;

// Effective input gain on mobile/tablet at the default slider position. This is
// the ONE number to tune when phones/tablets need a bigger waveform; the
// multiplier below is derived from it so the whole slider range scales with it.
// History: 2.8 (2x) -> 3.5 (1.2.112) -> 4.0 (1.2.113) -> back to 2.8 (1.2.114)
// -> 4.0 (1.2.116). The 1.2.112/1.2.113 increases looked like they did nothing,
// which turned out to be the broken iPad detection described on
// isMobileOrTablet above rather than the value being too small: on iPadOS the
// multiplier was never applied at all. Detection was fixed in 1.2.115 and the
// retune restarted from the 2.8 baseline, so this 4.0 is the first increase
// that actually reaches every phone and tablet.
const MOBILE_GAIN_AT_DEFAULT = 4.0;
const MOBILE_GAIN_MULTIPLIER =
  MOBILE_GAIN_AT_DEFAULT / DESKTOP_GAIN_BY_SLIDER[DEFAULT_GAIN_SLIDER];

const mapGainValue = (sliderValue) => {
  let gain = DESKTOP_GAIN_BY_SLIDER[sliderValue] || 1;

  // Boost the gain on mobile/tablet devices for better volume.
  if (isMobileOrTablet()) {
    gain = gain * MOBILE_GAIN_MULTIPLIER;
  }

  return gain;
}

// Boost added on top of mapGainValue at the recording input GainNode (see the
// INPUT_GAIN_BOOST history comment in newestillvoiceprogram/index2.js). Shared
// so the SPL calibration modal can match the main page's effective input gain.
//
// 0 since 1.2.94: the 2026-07/08 boost experiments (0.5 -> 1.5 -> 1.0/1.5
// split) were tuned while the loudness meter read 20*log10(gain) dB high (the
// calibration/measurement gain mismatch fixed in 1.2.84). With the compensated
// calibration, loudness is gain-independent, so the boost's only remaining
// effects were an oversized waveform and earlier clipping at true full voice.
// Input gain is back to mapGainValue alone (slider 5: desktop 1.4, mobile 2.8
// via its built-in multiplier). Raising this again is safe for loudness: the
// compensation self-corrects at every consumer.
const getInputGainBoost = () => 0;

// dB that the input GainNode adds to the analyzed signal, for a given effective
// linear gain (mapGainValue(slider) + getInputGainBoost()).
//
// The SPL calibration take is measured on the RAW mic: splCalibration/index.js
// wires mediaStream straight into its worklet, while the main page analyzes
// mediaStream -> gainNode -> worklet. So the stored calibration offset
// (90 - dB of the raw take) is referenced to an UNGAINED signal but gets added
// to dB computed from a GAINED one, putting every loudness reading
// 20*log10(gain) dB high (desktop slider 5: +7.6 dB, mobile: +12.7 dB).
// Subtracting this from the offset before arming the analyzer cancels that, and
// keeps loudness independent of the input gain slider, the mobile gain multiplier
// in mapGainValue, and any future INPUT_GAIN_BOOST retune.
// ---------------------------------------------------------------------------
// Pitch sensitivity
// ---------------------------------------------------------------------------

// localStorage key for the pitch gate's hard floor, per target position.
//
// Deliberately NOT the legacy `pitch_rms_threshold_*` key. That value drove a
// FIXED RMS gate (default 0.05, about -26 dBFS) that a frame had to clear before
// the pitch algorithm ran at all, whatever the microphone, input gain or room.
// It is why a quiet voice vanished from the Pitch trace while staying clearly
// visible in the Spectrogram. The gate is now relative to a tracked noise floor,
// and this value is only the absolute floor beneath it, so the two numbers are
// on different scales. Reusing the old key would silently apply a legacy 0.05 as
// a -26 dBFS floor and reproduce the original bug.
const pitchHardFloorKey = (position) => `pitch_hard_floor_${position || 'single'}`;

const PITCH_SENSITIVITY_DEFAULT_SLIDER = 50;
// Least sensitive end: -40 dBFS. Most sensitive end: -66 dBFS.
const PITCH_HARD_FLOOR_MAX = 0.01;
const PITCH_HARD_FLOOR_MIN = 0.0005;

// Slider 1..100 -> hard floor, logarithmically (the ear and the dB scale are both
// logarithmic, so a linear map would waste most of the travel at the loud end).
// 1 = least sensitive (0.01), 100 = most sensitive (0.0005), 50 = ~0.0022
// (about -53 dBFS), which is the shipped default.
const mapPitchSensitivity = (sliderValue) => {
  const clamped = Math.min(100, Math.max(1, sliderValue));
  const t = (clamped - 1) / 99;
  return PITCH_HARD_FLOOR_MAX * Math.pow(PITCH_HARD_FLOOR_MIN / PITCH_HARD_FLOOR_MAX, t);
};

// Inverse of mapPitchSensitivity, for restoring the slider from a stored floor.
const pitchSensitivityToSlider = (floor) => {
  const value = parseFloat(floor);
  if (!Number.isFinite(value) || value <= 0) return PITCH_SENSITIVITY_DEFAULT_SLIDER;
  const clamped = Math.min(PITCH_HARD_FLOOR_MAX, Math.max(PITCH_HARD_FLOOR_MIN, value));
  const t =
    Math.log(clamped / PITCH_HARD_FLOOR_MAX) /
    Math.log(PITCH_HARD_FLOOR_MIN / PITCH_HARD_FLOOR_MAX);
  return Math.round(1 + t * 99);
};

// The floor to send to the analyzer for a position: the stored setting, or the
// default when nothing has been saved.
const storedPitchHardFloor = (position) => {
  try {
    const raw = localStorage.getItem(pitchHardFloorKey(position));
    if (raw === null) return mapPitchSensitivity(PITCH_SENSITIVITY_DEFAULT_SLIDER);
    const value = parseFloat(raw);
    return Number.isFinite(value)
      ? value
      : mapPitchSensitivity(PITCH_SENSITIVITY_DEFAULT_SLIDER);
  } catch (e) {
    return mapPitchSensitivity(PITCH_SENSITIVITY_DEFAULT_SLIDER);
  }
};

const getInputGainDb = (effectiveGain) =>
  effectiveGain > 0 ? 20 * Math.log10(effectiveGain) : 0;

const calcTimeLength = (timeframe) => {
	let signalSize = 4
	let segmentsCount = 1024
	let oL= 512;
	let interval = 40; // Used for plotting duration for scheduling static plots

	if(timeframe === 40) {
		signalSize = 1024;
		segmentsCount = 2;
		oL=1024;
		interval = 10;
	} 
	else if(timeframe === 20) {
		signalSize = 512;
		segmentsCount = 4;
		interval = 10;
	} 
	else if(timeframe === 10) {
		// signalSize = 256;
		// segmentsCount = 8;
		// oL = 256;
		// interval = 40;
		signalSize = 256;
		segmentsCount = 8;
		oL = 256;
		interval = 40;
	}

	return {
		signalSize: signalSize,
		segmentsCount: segmentsCount,
		oL:oL,
		interval:interval,
	}
};

/**
 * Finds peaks in an array of data.
 *
 * @param {number[]} data - The array of data to search for peaks.
 * @param {Object} [options] - The options for finding peaks.
 * @param {number} [options.threshold=0.1] - The minimum value for a peak to be considered.
 * @param {number} [options.prominence=1.0] - The minimum difference between a peak and its neighboring points.
 * @param {number} [options.width=5] - The minimum width of a peak.
 * @returns {Object[]} - An array of peak objects, each containing the index and amplitude of a peak.
 */
function findPeaks(data, options = {}) {
  const {
    threshold = 0.1,
    prominence = 1.0,
    width = 5,
  } = options;

  const peaks = [];

  for (let i = 1; i < data.length - 1; i++) {
    const current = data[i];
    const prev = data[i - 1];
    const next = data[i + 1];

    if (
      current > prev &&
      current > next &&
      current >= threshold &&
      current - Math.min(prev, next) >= prominence
    ) {
      let leftIndex = i - 1;
      let rightIndex = i + 1;

      while (leftIndex > 0 && data[leftIndex] < data[leftIndex + 1]) {
        leftIndex--;
      }

      while (rightIndex < data.length - 1 && data[rightIndex] < data[rightIndex - 1]) {
        rightIndex++;
      }

      const peakWidth = rightIndex - leftIndex;

      if (peakWidth >= width) {
        peaks.push({
          index: i,
          amplitude: current,
        });
      }
    }
  }

  return peaks;
}

const isSignificantSignalPresent = (tdAudioDataArray, fftDataArray) => {
  // Time domain energy
  // const tdEnergy = Math.abs(tdAudioDataArray.reduce((sum, val) => sum + val, 0));
  // const tdThreshold = 0.07; // Adjust this value based on your microphone sensitivity

  // FFT energy (excluding very low frequencies which might be DC offset or low rumble)
  const fftEnergy = Math.abs(fftDataArray.slice(5).reduce((sum, val) => sum + val, 0));
  const fftThreshold = 255000; // Adjust this value based on your FFT scaling

  //console.log("HasFftEnergy:", fftEnergy);

  // Check if both time domain and FFT energies are above their respective thresholds
  return fftEnergy < fftThreshold;
};

const getPitchFrequency = (y, canvasHeight) => {
  // This is the inverse of getPixelValue - converts Y position back to frequency
  // Use the SAME linear logic as axes.js and getPixelValue
  
  const topPadding = 7;
  const availableHeight = canvasHeight - topPadding;
  const markerValues = [1760, 880, 440, 220, 110, 55]; // Top to bottom
  const numSections = markerValues.length - 1; // 5 sections
  const sectionHeight = availableHeight / numSections;
  
  // Handle out of bounds
  if (y < topPadding || y >= canvasHeight) {
    return 0;
  }
  
  // Find which section this Y position is in
  const adjustedY = y - topPadding;
  const sectionIndex = Math.floor(adjustedY / sectionHeight);
  
  // Safety check
  if (sectionIndex < 0 || sectionIndex >= numSections) {
    return 0;
  }
  
  // Linear interpolation within the section
  const upperFreq = markerValues[sectionIndex];
  const lowerFreq = markerValues[sectionIndex + 1];
  
  const yWithinSection = adjustedY - (sectionIndex * sectionHeight);
  const ratio = yWithinSection / sectionHeight;
  
  const frequency = upperFreq - (ratio * (upperFreq - lowerFreq));
  
  return Math.round(frequency);
};

function linearInterpolate(y1, y2, mu) {
  return y1 * (1 - mu) + y2 * mu;
}

const scaleHnrRatio = (hnrRatio) => {
  const minHnrRatio = 1.45;
  const maxHnrRatio = 1.88;
  
  const scaledHnrRatio = (hnrRatio - minHnrRatio) / (maxHnrRatio - minHnrRatio) * 100;

  // Ensure the scaled ratio is within the range of 0 to 100
  const clampedHnrRatio = Math.min(Math.max(scaledHnrRatio, 0), 100);

  return clampedHnrRatio;
};

export default {
  isMobileOrTablet,
  configureCursorSelection,
  calculateTimeInterval,
  getColorForValue,
  movingAverage,
  calculateResonanceSpectrum,
  frequencyToNote,
  calculateSPLInstance,
  calculateIntensity,
  autoCorrelate,
  getPixelValue,
  performFFT,
  calcTimeLength,
  findPeaks,
  isSignificantSignalPresent,
  getPitchFrequency,
  linearInterpolate,
  scaleHnrRatio,
  getNormalizedGain,
  mapGainValue,
  mapPitchSensitivity,
  pitchSensitivityToSlider,
  pitchHardFloorKey,
  storedPitchHardFloor,
  PITCH_SENSITIVITY_DEFAULT_SLIDER,
  getInputGainBoost,
  getInputGainDb,
  applyBlackmanWindow
};
